// Ported from genieos/web/src/components/historian/SelectedVariablesPanel.tsx @ 84923d036
/**
 * SelectedVariablesPanel Component
 *
 * Shows selected variables with color coding, alias editing, and removal.
 * Includes query execution button and results summary.
 */

import React, { useState, useCallback } from 'react';
import {
  ListChecks,
  X,
  Edit3,
  Check,
  Trash2,
  Play,
  Loader2,
  AlertCircle,
  BarChart3,
} from 'lucide-react';
import { useHistorian, type SelectedVariable } from '@/contexts/HistorianContext';
import { HISTORIAN_MAX_SERIES } from './historianConstants';
import { cn } from '@/utils/cn';
import { Button, Card } from '@/components/gamma';

interface SelectedVariablesPanelProps {
  className?: string;
  onViewChart?: () => void;
}

export const SelectedVariablesPanel: React.FC<SelectedVariablesPanelProps> = ({
  className,
  onViewChart,
}) => {
  const {
    state,
    removeVariable,
    clearVariables,
    updateVariableAlias,
    updateVariableColor,
    executeQuery,
  } = useHistorian();

  const { selectedVariables, queryLoading, queryError, queryResult } = state;

  // Track which variable is being edited
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  // Start editing alias
  const handleStartEdit = useCallback((variable: SelectedVariable) => {
    setEditingId(variable.id);
    setEditValue(variable.alias);
  }, []);

  // Save edited alias
  const handleSaveEdit = useCallback(() => {
    if (editingId && editValue.trim()) {
      updateVariableAlias(editingId, editValue.trim());
    }
    setEditingId(null);
    setEditValue('');
  }, [editingId, editValue, updateVariableAlias]);

  // Cancel editing
  const handleCancelEdit = useCallback(() => {
    setEditingId(null);
    setEditValue('');
  }, []);

  // Handle key press in edit input
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSaveEdit();
      } else if (e.key === 'Escape') {
        handleCancelEdit();
      }
    },
    [handleSaveEdit, handleCancelEdit]
  );

  // Color picker options
  const colorOptions = [
    '#3b82f6', // blue
    '#10b981', // emerald
    '#f59e0b', // amber
    '#ef4444', // red
    '#8b5cf6', // violet
    '#ec4899', // pink
    '#06b6d4', // cyan
    '#84cc16', // lime
  ];

  return (
    <Card variant="default" className={cn('flex flex-col', className)}>
      {/* Header */}
      <div className="p-4 border-b border-ems-border">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-ems-text-primary flex items-center gap-2">
            <ListChecks className="w-4 h-4 text-ems-blue" />
            Selected Variables
            <span className="text-ems-text-secondary font-normal">
              ({selectedVariables.length}/{HISTORIAN_MAX_SERIES})
            </span>
          </h3>
          {selectedVariables.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearVariables}
              className="text-ems-red hover:bg-ems-red/10"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Variables list */}
      <div className="flex-1 overflow-y-auto p-2 min-h-[200px]">
        {selectedVariables.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center py-8">
            <BarChart3 className="w-12 h-12 text-ems-text-secondary/30 mb-3" />
            <p className="text-sm text-ems-text-secondary">No variables selected</p>
            <p className="text-xs text-ems-text-secondary/70 mt-1">
              Click registers in the browser to add
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {selectedVariables.map((variable) => (
              <VariableItem
                key={variable.id}
                variable={variable}
                isEditing={editingId === variable.id}
                editValue={editValue}
                onEditValueChange={setEditValue}
                onStartEdit={() => handleStartEdit(variable)}
                onSaveEdit={handleSaveEdit}
                onCancelEdit={handleCancelEdit}
                onKeyDown={handleKeyDown}
                onRemove={() => removeVariable(variable.id)}
                onColorChange={(color) =>
                  updateVariableColor(variable.id, color)
                }
                colorOptions={colorOptions}
              />
            ))}
          </div>
        )}
      </div>

      {/* Query controls */}
      <div className="p-4 border-t border-ems-border space-y-3">
        {/* Error message */}
        {queryError && (
          <div className="flex items-center gap-2 p-2 rounded-lg bg-ems-red/10 text-ems-red text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{queryError.message}</span>
          </div>
        )}

        {/* Query result summary */}
        {queryResult && (
          <div className="text-xs text-ems-text-secondary space-y-1 p-2 rounded-lg bg-ems-bg-card-hover">
            <div className="flex justify-between">
              <span>Data points:</span>
              <span className="text-ems-text-primary font-medium">
                {queryResult.metadata.pointCount.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span>Query time:</span>
              <span className="text-ems-text-primary font-medium">
                {queryResult.metadata.queryTimeMs}ms
              </span>
            </div>
            {/*
              The raw interval reports downsampledFrom == pointCount, because
              nothing was downsampled (internal/api/handlers/historian.go states
              this: reporting an estimate there would be "a claim, not a
              placeholder"). A `> 0` guard renders that as "Downsampled from:
              ~18k" on a response that downsampled nothing, so the comparison
              has to be against pointCount — which is what StatusBar has always
              done. Demsv1.0#3996.
            */}
            {queryResult.metadata.pointCount <
              queryResult.metadata.downsampledFrom && (
              <div className="flex justify-between">
                <span>Downsampled from:</span>
                <span className="text-ems-text-primary font-medium">
                  ~{(queryResult.metadata.downsampledFrom / 1000).toFixed(0)}k
                </span>
              </div>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={executeQuery}
            disabled={selectedVariables.length === 0 || queryLoading}
            className="flex-1"
            leftIcon={
              queryLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Play className="w-4 h-4" />
              )
            }
          >
            {queryLoading ? 'Querying...' : 'Execute Query'}
          </Button>

          {queryResult && onViewChart && (
            <Button
              variant="secondary"
              size="sm"
              onClick={onViewChart}
              leftIcon={<BarChart3 className="w-4 h-4" />}
            >
              View Chart
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
};

// ============================================================================
// Variable Item
// ============================================================================

interface VariableItemProps {
  variable: SelectedVariable;
  isEditing: boolean;
  editValue: string;
  onEditValueChange: (value: string) => void;
  onStartEdit: () => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onRemove: () => void;
  onColorChange: (color: string) => void;
  colorOptions: string[];
}

const VariableItem: React.FC<VariableItemProps> = ({
  variable,
  isEditing,
  editValue,
  onEditValueChange,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onKeyDown,
  onRemove,
  onColorChange,
  colorOptions,
}) => {
  const [showColorPicker, setShowColorPicker] = useState(false);

  return (
    <div
      className={cn(
        'flex items-center gap-2 p-2 rounded-lg',
        'bg-ems-bg-card-hover border border-ems-border',
        'group'
      )}
    >
      {/* Color indicator */}
      <div className="relative">
        <button
          onClick={() => setShowColorPicker(!showColorPicker)}
          className="w-3 h-3 rounded-full cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-offset-glass-light hover:ring-white/20"
          style={{ backgroundColor: variable.color }}
          title="Change color"
          data-testid="historian-series-swatch"
        />
        {showColorPicker && (
          <div className="absolute left-0 top-5 z-50 p-2 rounded-lg bg-ems-bg-card border border-ems-border shadow-lg">
            <div className="grid grid-cols-4 gap-1">
              {colorOptions.map((color) => (
                <button
                  key={color}
                  onClick={() => {
                    onColorChange(color);
                    setShowColorPicker(false);
                  }}
                  className={cn(
                    'w-5 h-5 rounded-full',
                    'hover:ring-2 hover:ring-offset-1 hover:ring-white/20',
                    color === variable.color && 'ring-2 ring-white'
                  )}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Alias / Edit input */}
      <div className="flex-1 min-w-0">
        {isEditing ? (
          <div className="flex items-center gap-1">
            <input
              type="text"
              value={editValue}
              onChange={(e) => onEditValueChange(e.target.value)}
              onKeyDown={onKeyDown}
              autoFocus
              className={cn(
                'flex-1 px-2 py-0.5 rounded text-sm',
                'bg-ems-bg-card border-ems-blue/50',
                'text-ems-text-primary',
                'focus:outline-none focus:ring-1 focus:ring-ems-blue'
              )}
            />
            <button
              onClick={onSaveEdit}
              className="p-1 text-ems-green hover:bg-ems-green/10 rounded"
            >
              <Check className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={onCancelEdit}
              className="p-1 text-ems-red hover:bg-ems-red/10 rounded"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <button
            onClick={onStartEdit}
            className="flex items-center gap-1.5 text-sm text-ems-text-primary hover:text-ems-blue transition-colors group/edit"
          >
            {/* Read-only label: `alias` is the sanitised, possibly-suffixed wire
                key (HistorianContext.tsx) — not fit to render. `displayLabel`
                is what the operator expects; `alias` stays the value the edit
                input below seeds and writes back, since the alias genuinely
                is what's being edited there (CodeRabbit finding on #813). */}
            <span className="truncate">{variable.displayLabel ?? variable.alias}</span>
            <Edit3 className="w-3 h-3 opacity-0 group-hover/edit:opacity-100 transition-opacity" />
          </button>
        )}
        <div className="text-xs text-ems-text-secondary truncate mt-0.5">
          {variable.siteName} / {variable.deviceLabel} / {variable.metric} ({variable.unit ?? '—'})
        </div>
      </div>

      {/* Remove button */}
      <button
        onClick={onRemove}
        className="p-1 text-ems-text-secondary hover:text-ems-red hover:bg-ems-red/10 rounded opacity-0 group-hover:opacity-100 transition-opacity"
        title="Remove variable"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};
